import React, { useRef, useEffect } from 'react';
import { Bold, List } from 'lucide-react';

const ALLOWED_TAGS = new Set(['B', 'STRONG', 'I', 'EM', 'U', 'UL', 'OL', 'LI', 'BR', 'P', 'DIV', 'SPAN']);
const DROP_TAGS = new Set(['SCRIPT', 'STYLE', 'NOSCRIPT', 'IFRAME', 'OBJECT', 'EMBED']);

// Whitelist sanitizer — strips scripts, event handlers, and any tag/attr not allowed.
// Content is admin-authored, but we sanitize on render to prevent any script injection.
export const sanitizeHtml = (html) => {
  if (!html || typeof html !== 'string') return '';
  const doc = new DOMParser().parseFromString(`<div>${html}</div>`, 'text/html');
  const walk = (node) => {
    [...node.childNodes].forEach((child) => {
      if (child.nodeType === 1) {
        if (DROP_TAGS.has(child.tagName)) {
          child.remove(); // drop element AND its content entirely
          return;
        }
        if (!ALLOWED_TAGS.has(child.tagName)) {
          // unwrap disallowed (but harmless) element: keep its text/children
          const text = doc.createTextNode(child.textContent || '');
          child.replaceWith(text);
          return;
        }
        [...child.attributes].forEach((a) => child.removeAttribute(a.name));
        walk(child);
      }
    });
  };
  const root = doc.body.firstChild;
  walk(root);
  return root.innerHTML;
};

export const RichTextEditor = ({ value, onChange, placeholder, testId }) => {
  const ref = useRef(null);

  useEffect(() => {
    if (ref.current && ref.current.innerHTML !== (value || '')) {
      ref.current.innerHTML = value || '';
    }
  }, []); // initialize once from value

  const exec = (cmd) => {
    if (cmd === 'bold') { try { document.execCommand('styleWithCSS', false, false); } catch (_) {} }
    document.execCommand(cmd, false, null);
    ref.current?.focus();
    onChange(ref.current?.innerHTML || '');
  };

  return (
    <div className="border border-slate-300 rounded-lg overflow-hidden bg-white" data-testid={testId}>
      <div className="flex items-center gap-1 border-b border-slate-200 bg-slate-50 px-2 py-1">
        <button type="button" onMouseDown={(e) => { e.preventDefault(); exec('bold'); }}
          className="p-1.5 rounded hover:bg-slate-200 text-slate-700" title="Bold" data-testid={testId ? `${testId}-bold` : undefined}>
          <Bold className="w-4 h-4" />
        </button>
        <button type="button" onMouseDown={(e) => { e.preventDefault(); exec('insertUnorderedList'); }}
          className="p-1.5 rounded hover:bg-slate-200 text-slate-700" title="Bullet list" data-testid={testId ? `${testId}-bullets` : undefined}>
          <List className="w-4 h-4" />
        </button>
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={() => onChange(ref.current?.innerHTML || '')}
        data-placeholder={placeholder}
        className="min-h-[110px] px-3 py-2 text-sm font-normal text-slate-800 outline-none prose-sm [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_b]:font-bold [&_strong]:font-bold empty:before:content-[attr(data-placeholder)] empty:before:text-slate-400"
      />
    </div>
  );
};

// Read-only renderer for sanitized policy HTML.
export const RichTextView = ({ html, className = '' }) => (
  <div
    className={`text-sm font-normal text-slate-600 leading-relaxed [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:mb-1 [&_strong]:font-bold [&_b]:font-bold ${className}`}
    dangerouslySetInnerHTML={{ __html: sanitizeHtml(html) }}
  />
);

export default RichTextEditor;
